import { ReactElement } from "react";
import { Box, Heading, SkeletonLoader, Text } from "@twilio-paste/core";
import { ProcessErrorIcon } from "@twilio-paste/icons/esm/ProcessErrorIcon";
import { ProcessWarningIcon } from "@twilio-paste/icons/esm/ProcessWarningIcon";
import { ProcessSuccessIcon } from "@twilio-paste/icons/esm/ProcessSuccessIcon";

import { constants, copy, types } from "./definitions";
import styles from "./SystemStatus.module.css";
import CompositeCallout from "../compositeCallout";
import TrackingAnchor from "../trackingAnchor";

const statusToIconMap = {
  [types.SystemStatusTypes.error]: (): ReactElement => (
    <ProcessErrorIcon color="colorTextIconError" decorative />
  ),
  [types.SystemStatusTypes.success]: (): ReactElement => (
    <ProcessSuccessIcon color="colorTextIconSuccess" decorative />
  ),
  [types.SystemStatusTypes.warning]: (): ReactElement => (
    <ProcessWarningIcon color="colorTextIconWarning" decorative />
  ),
};

const SystemStatus = ({
  hasError,
  loading = false,
  loadingCount = constants.DEFAULT_STATUS_LOADING_COUNT,
  statusPageUrl,
  systemStatuses,
}: types.SystemStatusProps): ReactElement => {
  const renderStatus = ({
    name,
    statusType,
  }: types.SystemStatus): ReactElement => {
    const renderIconComponent = statusToIconMap[statusType];

    return (
      <Box as="div" key={name} className={styles.statusListItem}>
        {renderIconComponent()}

        <Text as="p" fontSize="fontSize30" marginLeft="space30">
          {name}
        </Text>
      </Box>
    );
  };

  const renderLoadingSkeletons = (): ReactElement[] => {
    const loadingSkeletons: JSX.Element[] = [];

    for (let i = 0; i < loadingCount; i++) {
      loadingSkeletons.push(
        <SkeletonLoader
          key={`system-skeleton-${i}`}
          height="1.25rem"
          width="10rem"
        />
      );
    }

    return loadingSkeletons;
  };

  return (
    <Box as="div" className={styles.systemStatus}>
      <Heading as="h3" variant="heading30" marginBottom="space0">
        {copy.title}
      </Heading>

      {!hasError && loading && (
        <Box as="div" className={styles.statusList}>
          {renderLoadingSkeletons()}
        </Box>
      )}

      {!loading && hasError && (
        <CompositeCallout
          description={copy.error.description}
          heading={copy.error.heading}
          variant="warning"
        />
      )}

      {!hasError && !loading && systemStatuses && (
        <Box as="div" className={styles.statusList}>
          {systemStatuses.map((status) => renderStatus(status))}
        </Box>
      )}

      <TrackingAnchor href={statusPageUrl} showExternal>
        {copy.detailedStatusUrl}
      </TrackingAnchor>
    </Box>
  );
};

export default SystemStatus;
